import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Chip, Stack, Button, Tooltip } from '@mui/material';
import { callApi } from '../../utils/apiCall';
import { dispatch } from '../../redux/store';
import { getAppointment } from '../../redux/slices/lead/appointmentThunks';
import useAppointmentRequestScope from '../../hooks/useAppointmentRequestScope';

const PRESENTATION = {
  synced: ['Cal.com · Synced', 'success'],
  pending: ['Cal.com · Change pending', 'info'],
  failed: ['Cal.com · Change failed', 'error'],
  unknown: ['Cal.com · Needs review', 'warning'],
  unlinked: ['Cal.com · Booking link needed', 'warning'],
};

export default function CalcomAppointmentBadge({ calcom, practiceId, leadId, appointmentId }) {
  const scope = useAppointmentRequestScope(practiceId, leadId);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  useEffect(() => {
    generation.current += 1;
    setRefreshing(false);
    setError('');
    return () => { generation.current += 1; };
  }, [practiceId, leadId, appointmentId]);

  useEffect(() => {
    if (calcom?.sync_status !== 'pending') return undefined;
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      dispatch(getAppointment(practiceId, leadId, scope));
      if (attempts >= 6) clearInterval(timer);
    }, 5000);
    return () => clearInterval(timer);
  }, [calcom?.sync_status, practiceId, leadId, scope]);

  if (!calcom) return null;
  const [label, color] = PRESENTATION[calcom.sync_status] || PRESENTATION.unknown;
  const refresh = async () => {
    const capturedGeneration = generation.current;
    setRefreshing(true);
    setError('');
    try {
      const result = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/appointments/${appointmentId}/calcom-refresh/`, 'post', {});
      if (generation.current !== capturedGeneration) return;
      if (![200, 202].includes(result?.status)) setError(result?.data?.detail || 'Unable to check Cal.com. Try again.');
      dispatch(getAppointment(practiceId, leadId, scope));
      if (result?.status === 202) {
        for (let attempt = 0; attempt < 6; attempt += 1) {
          // Poll serially: parallel reads cannot observe the queued reconciliation.
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 5000));
          if (generation.current !== capturedGeneration) return;
          // eslint-disable-next-line no-await-in-loop
          const checked = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/appointments/${appointmentId}/`);
          if (generation.current !== capturedGeneration) return;
          dispatch(getAppointment(practiceId, leadId, scope));
          const summary = checked?.data?.calcom;
          if (summary?.sync_status === 'synced'
            || (summary?.last_synced_at && summary.last_synced_at !== calcom.last_synced_at)) break;
        }
      }
    } catch {
      if (generation.current === capturedGeneration) setError('Unable to check Cal.com. Try again.');
    } finally {
      if (generation.current === capturedGeneration) setRefreshing(false);
    }
  };
  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Tooltip title={error || calcom.last_error || (calcom.sync_status === 'pending' ? 'The appointment updates after Cal.com confirms the change.' : 'Appointment booked through Cal.com.')}>
        <Chip size="small" color={color} variant="outlined" label={label} />
      </Tooltip>
      {['pending', 'failed', 'unknown'].includes(calcom.sync_status) && (
        <Button size="small" disabled={refreshing} onClick={refresh}>{refreshing ? 'Checking…' : 'Refresh Cal.com status'}</Button>
      )}
      {error && <span role="alert">{error}</span>}
    </Stack>
  );
}

CalcomAppointmentBadge.propTypes = {
  calcom: PropTypes.shape({ sync_status: PropTypes.string, last_error: PropTypes.string, last_synced_at: PropTypes.string }),
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  appointmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
CalcomAppointmentBadge.defaultProps = { calcom: null };
